import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../lib/api';
import { fetchGraphPhotoBlob } from '../lib/graph';
import { devAuthEnabled } from '../lib/env';

interface UserAvatarContextValue {
  /** Object URL for the resolved avatar image, or null to fall back to initials. */
  photoUrl: string | null;
  /** True while the avatar is still being resolved. */
  loading: boolean;
  /** Re-resolve the avatar (e.g. after the user uploads a new custom picture). */
  refresh: () => void;
}

const UserAvatarContext = createContext<UserAvatarContextValue | null>(null);

/**
 * Resolves the signed-in user's avatar exactly once and shares the resulting
 * object URL across the app (Topbar, Profile, etc.) so it isn't refetched per
 * render or per component.
 *
 * Resolution order matches the My Profile page:
 *   1. custom upload  — GET /api/me/avatar (auth-protected blob)
 *   2. Microsoft 365  — Graph /me/photo/$value
 *   3. initials       — handled downstream by <Avatar> when photoUrl is null
 *
 * In dev-auth mode there is no MSAL account, so we skip both network calls and
 * let consumers render initials.
 */
export function UserAvatarProvider({ children }: { children: ReactNode }) {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!devAuthEnabled);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (devAuthEnabled) {
      setPhotoUrl(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    const controller = new AbortController();
    setLoading(true);

    void (async () => {
      // Best-effort: custom upload first, then the Microsoft 365 Graph photo.
      let blob: Blob | null = null;
      try {
        blob = await api.fetchAvatarBlob(controller.signal);
      } catch {
        // ignore — fall through to Graph
      }
      if (!blob && !cancelled) {
        blob = await fetchGraphPhotoBlob(controller.signal);
      }
      if (cancelled) return;
      if (blob) {
        objectUrl = URL.createObjectURL(blob);
        setPhotoUrl(objectUrl);
      } else {
        setPhotoUrl(null);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [refreshKey]);

  const value = useMemo<UserAvatarContextValue>(
    () => ({ photoUrl, loading, refresh: () => setRefreshKey((n) => n + 1) }),
    [photoUrl, loading],
  );

  return <UserAvatarContext.Provider value={value}>{children}</UserAvatarContext.Provider>;
}

export function useUserAvatar(): UserAvatarContextValue {
  const ctx = useContext(UserAvatarContext);
  if (!ctx) throw new Error('useUserAvatar must be used within <UserAvatarProvider>');
  return ctx;
}
